<template>
  <FormCard :title="mode === 'edit' ? 'Данные компании' : 'Компания'" source="customer">
    <FormGrid :cols="singleColumn ? 1 : 2">
      <FormField
        label="ИНН"
        help="Введите ИНН (10 или 12 цифр) — данные подтянутся из DaData"
        :invalid="(!form.inn.valid && form.inn.tochted) || !!errors.inn"
        :error="errors.inn || fieldError(form.inn)"
      >
        <input
          id="company-inn"
          v-model="form.inn.value"
          type="text"
          :class="{ invalid: errors.inn }"
          @blur="onInnBlur"
          @input="onInnInput"
        />
      </FormField>
      <FormField
        label="Название"
        help="Можно ввести часть названия — подскажет DaData"
        :invalid="(!form.name.valid && form.name.tochted) || !!errors.name"
        :error="errors.name || fieldError(form.name)"
      >
        <input
          id="company-name"
          v-model="form.name.value"
          type="text"
          :class="{ invalid: errors.name }"
          @blur="form.name.blur"
          @input="onCompanyNameInput"
        />
      </FormField>
      <FormField
        label="Директор"
        :invalid="(!form.director.valid && form.director.tochted) || !!errors.director"
        :error="errors.director || fieldError(form.director)"
      >
        <input
          id="company-director"
          v-model="form.director.value"
          type="text"
          :class="{ invalid: errors.director }"
          @blur="form.director.blur"
        />
      </FormField>
      <FormField
        label="ОГРН/ОГРНИП"
        :invalid="(!form.ogrn.valid && form.ogrn.tochted) || !!errors.ogrn"
        :error="errors.ogrn || fieldError(form.ogrn)"
      >
        <input
          id="company-ogrn"
          v-model="form.ogrn.value"
          type="text"
          :class="{ invalid: (!form.ogrn.valid && form.ogrn.tochted) || errors.ogrn }"
          @blur="form.ogrn.blur"
        />
      </FormField>
      <FormField label="Черный список">
        <input
          id="company-blacklist"
          v-model="form.blackList.value"
          type="checkbox"
          @blur="form.blackList.blur"
        />
      </FormField>
    </FormGrid>

    <div class="form-actions">
      <my-shrunk-button
        @click="sendForm"
        :btnSettings="{shrunk:true,name:''}"
      >{{ mode === 'edit' ? 'Сохранить' : 'Создать' }}</my-shrunk-button>
      <my-shrunk-button
        class="cancel-btn bg-denied"
        @click="closeForm"
        :btnSettings="{shrunk:true,name:''}"
      >Отменить</my-shrunk-button>
    </div>

    <div v-if="showSuggestionsModal" class="suggestions-modal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>Выберите компанию</h3>
          <button @click="closeModal">&times;</button>
        </div>
        <div class="modal-body">
          <div
            v-for="(suggestion, index) in companySuggestions"
            :key="index"
            class="suggestion-item"
            @click="selectCompany(suggestion.data)"
          >
            <div class="company-name">{{ suggestion.value }}</div>
            <div class="company-details">
              <span v-if="suggestion.data.inn">ИНН: {{ suggestion.data.inn }}</span>
              <span v-if="suggestion.data.address">Адрес: {{ suggestion.data.address.value }}</span>
            </div>
          </div>
          <div v-if="!companySuggestions.length" class="no-results">
            Ничего не найдено
          </div>
        </div>
      </div>
    </div>
  </FormCard>
</template>


<script>
import { useForm, useFormat, touchForm } from '@/hooks/form'
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'

export default {
  name: 'CompanyForm',
  props: {
    mode: { type: String, default: 'create' },
    initialData: { type: Object, default: () => ({}) },
    errors: { type: Object, default: () => ({}) },
    singleColumn: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const isEdit = computed(() => props.mode === 'edit')

    const form = useForm({
      inn: {
        value: '',
        validators: ['required', 'inn'],
      },
      name: {
        value: '',
        validators: ['required'],
      },
      director: {
        value: '',
        validators: ['required'],
      },
      ogrn: {
        value: '',
        validators: ['required', 'ogrn'],
      },
      blackList: {
        value: false,
        validators: [],
      },
    })

    const fillInitialData = (source = {}) => {
      if (!isEdit.value) return
      form.inn.value = source.inn || ''
      form.name.value = source.name || ''
      form.director.value = source.director || ''
      form.ogrn.value = source.ogrn || ''
      form.blackList.value = Boolean(source.blackList ?? source.black_list ?? false)
    }
    watch(() => props.initialData, fillInitialData, { immediate: true })

    const store = useStore()
    const showSuggestionsModal = ref(false)
    const searchTimeout = ref(null)
    const companySuggestions = computed(() => store.getters['dadata/companySuggestions'])
    const isLoading = computed(() => store.getters['dadata/isLoading'])

    const fieldError = (field) => field.tochted
      ? Object.values(field.errors).find(Boolean) || ''
      : ''

    const sendForm = async () => {
      if (!await touchForm(form)) return
      const payload = useFormat(form)
      if (isEdit.value) {
        if (props.initialData?.id != null) payload.id = props.initialData.id
        emit('update', payload)
        return
      }
      emit('sendForm', { model: 'companies', form: payload })
    }

    const closeForm = () => {
      emit('closeForm')
    }

    const fillForm = (companyData) => {
      form.inn.value = companyData.inn || ''
      form.name.value = companyData.name?.short_with_opf || companyData.name?.full_with_opf || ''
      form.ogrn.value = companyData.ogrn || ''

      if (companyData.management) {
        form.director.value = `${companyData.management.name} (${companyData.management.post})`
      }
    }

    const onInnBlur = () => {
      form.inn.blur()
      if (form.inn.value && form.inn.value.length >= 10) {
        searchCompanies(form.inn.value, 'inn')
      }
    }

    const onInnInput = () => {
      if (form.inn.value.length === 10 || form.inn.value.length === 12) {
        searchCompanies(form.inn.value, 'inn')
      }
    }

    const onCompanyNameInput = () => {
      clearTimeout(searchTimeout.value)
      if (form.name.value.length > 2) {
        searchTimeout.value = setTimeout(() => {
          searchCompanies(form.name.value, 'name')
        }, 500)
      }
    }

    const searchCompanies = async (query, type) => {
      await store.dispatch('dadata/fetchCompanyData', { query, type })
      if (companySuggestions.value.length > 0) {
        showSuggestionsModal.value = true
      }
    }

    const selectCompany = (companyData) => {
      fillForm(companyData)
      closeModal()
    }

    const closeModal = () => {
      showSuggestionsModal.value = false
      store.dispatch('dadata/clearSuggestions')
    }

    return {
      form,
      fieldError,
      isLoading,
      showSuggestionsModal,
      companySuggestions,
      sendForm,
      closeForm,
      onInnBlur,
      onInnInput,
      onCompanyNameInput,
      selectCompany,
      closeModal,
      mode: props.mode,
      errors: props.errors,
    }
  },
}
</script>


<style scoped>
.form-actions {
  display: flex;
  gap: 20px;
  margin-top: 12px;
}

.cancel-btn {
  margin-left: 0;
}

.suggestions-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  padding: 20px;
  border-radius: 8px;
  width: 80%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.modal-header button {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
}

.suggestion-item {
  padding: 10px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
}

.suggestion-item:hover {
  background-color: #f5f5f5;
}

.company-name {
  font-weight: bold;
  margin-bottom: 5px;
}

.company-details {
  display: flex;
  flex-direction: column;
  font-size: 0.9rem;
  color: #666;
}

.no-results {
  padding: 15px;
  text-align: center;
  color: #666;
}
</style>
